/** Shared allowlisted agent representation. Never spread an upstream DTO. */
export const AGENT_ORIGIN = "https://www.bookaclassic.com";
export const US_BOOKING_ORIGIN = "https://driveshare.com";
export const US_BOOKING_NOTICE = "US listings book on DRIVESHARE.";
export const AGENT_CONTACT_EMAIL = "support@bookaclassic.com";
export const RENTAL_TYPES = ["driving", "chauffeured", "event"];
export function agentQuoteQuery(args) {
  for (const key of ["date_from", "date_to"]) {
    if (args[key] != null && (typeof args[key] !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(args[key]) ||
        new Date(`${args[key]}T00:00:00Z`).toISOString().slice(0,10) !== args[key])) {
      throw new Error(`Send ${key} as a valid YYYY-MM-DD date.`);
    }
  }
  if (["chauffeured", "event"].includes(args.rental_type) &&
      (!Number.isInteger(args.hours) || args.hours < 1 || args.hours > 240)) {
    throw new Error("Send integer hours=1..240 for chauffeured or event hire.");
  }
  if(args.rental_type==="chauffeured" && (args.hours>23 || (args.date_to && args.date_to!==args.date_from)))
    throw new Error("Chauffeured widget estimates support 1..23 integer hours on one calendar day.");
  const sp = new URLSearchParams();
  for (const key of ["car_id", "date_from", "date_to", "rental_type", "hours"]) {
    if (args[key] != null) sp.set(key, String(args[key]));
  }
  if (args.rental_type === "event") {
    if (!Number.isInteger(args.hours) || args.hours < 1 || args.hours > 240)
      throw new Error("Send integer hours=1..240 for event hire.");
    const start = new Date(`${args.date_from}T00:00:00Z`);
    if (!Number.isFinite(start.getTime())) throw new Error("Send date_from as YYYY-MM-DD.");
    const end = new Date(start.getTime() + args.hours * 3600000);
    const endDay = end.toISOString().slice(0,10);
    if (args.date_to && args.date_to !== endDay)
      throw new Error(`date_to must be ${endDay} for this event duration.`);
    sp.set("date_to", endDay);
    sp.set("pickup_time", "00:00");
    sp.set("dropoff_time", end.toISOString().slice(11,16));
  }
  return sp;
}
export function agentPublicOrigin(value = AGENT_ORIGIN) {
  if (value !== AGENT_ORIGIN) throw new Error("PUBLIC_BASE_URL must be https://www.bookaclassic.com");
  return value;
}
const number = value => typeof value === "number" && Number.isFinite(value) ? value : null;
const text = value => typeof value === "string" ? value : null;
const rounded = value => number(value) === null ? null : Math.round(value * 100) / 100;
export function safeListingUrl(car) {
  const country = typeof car.countryCode === "string" ? car.countryCode.toUpperCase() : "";
  if (!/^[A-Z]{2}$/.test(country)) throw new Error("Listing country is unavailable");
  const raw = car.canonicalUrl;
  if (typeof raw !== "string") throw new Error("Canonical listing identity unavailable");
  const url = new URL(raw);
  if (url.protocol !== "https:" || url.username || url.password || url.search || url.hash ||
      !["www.bookaclassic.com", "driveshare.com", "www.driveshare.com"].includes(url.hostname)) {
    throw new Error("Unsafe canonical listing URL");
  }
  // Identity paths come from the authoritative canonical reader, not this mapper.
  if (country !== "US") url.host = "www.bookaclassic.com";
  return url.href;
}
export function carSummary(car) {
  const country = text(car.countryCode)?.toUpperCase() ?? null;
  const rental_types = [
    car.rentalDrivingActive && "driving",
    car.rentalChauffeurActive && "chauffeured",
    car.rentalEventActive && "event",
  ].filter(Boolean);
  return {
    id: car.id, title: [car.year, car.make, car.model].filter(Boolean).join(" "),
    make: text(car.make), model: text(car.model), year: number(car.year),
    location: { city: text(car.city), region: text(car.state), country_code: country,
      lat: rounded(car.latitude), lng: rounded(car.longitude) },
    rental_types,
    prices: {
      currency: text(car.currencyCode),
      self_drive_per_day: rental_types.includes("driving") ? number(car.priceDriving) : null,
      chauffeur_per_hour: rental_types.includes("chauffeured") ? number(car.chauffeuredHourlyRate) : null,
      chauffeur_minimum: rental_types.includes("chauffeured") ? number(car.chauffeurMinPrice) || number(car.minimumPrice) || 0 : null,
      event_from: rental_types.includes("event") ? number(car.eventMinPrice) || number(car.minimumPrice) || 0 : null,
      event_per_hour: rental_types.includes("event") ? number(car.eventHourlyRate) : null,
      event_per_day: rental_types.includes("event") ? number(car.priceEvent) : null,
    },
    instant_book: car.instantBook === true,
    rating: number(car.reviewsRating), reviews_count: number(car.reviewsCount),
    image: text(car.primaryImageCdnMedium ?? car.primaryImageUrl),
    url: safeListingUrl(car), booking_platform: country === "US" ? "driveshare" : "bookaclassic",
    updated_at: text(car.updatedAt),
    ...(country === "US" ? { booking_notice: "US cars are booked on DRIVESHARE." } : {}),
  };
}
/** Adapter for the authoritative public detail reader's snake_case DTO. */
export function detailSummarySource(body) {
  const car = body.car;
  if (!car || car.public_unavailable || car.deleted_at || car.is_deleted === true ||
      (car.listing_status != null && car.listing_status !== "active")) throw new Error("Active car details unavailable");
  return {
    id: car.id, make: car.make, model: car.model, year: car.year, city: car.city, state: car.state,
    countryCode: car.country_code, currencyCode: car.currency_code,
    rentalDrivingActive: car.rental_driving_active, rentalChauffeurActive: car.rental_chauffeured_active,
    rentalEventActive: car.rental_event_active, priceDriving: car.price_driving, priceEvent: car.price_event,
    chauffeuredHourlyRate: car.chauffeured_hourly_rate, chauffeurMinPrice: car.chauffeured_min_price_hourly_rate,
    eventMinPrice: car.event_min_price_hourly_rate, eventHourlyRate: car.event_hourly_rate,
    minimumPrice: car.minimum_price, latitude: car.latitude, longitude: car.longitude,
    canonicalUrl: car.canonicalUrl, updatedAt: car.updated_at,
    reviewsRating: car.reviews_rating, reviewsCount: car.reviews_count,
    primaryImageUrl: body.images?.[0]?.cdn_medium ?? body.images?.[0]?.url,
    // A stored instant-rental toggle is not proof of the current booking decision.
    instantBook: body.instantBook === true,
  };
}
export function plainPublicText(value, max = 1500) {
  return typeof value === "string" ? value.replace(/<[^>]*>/g, " ")
    .replace(/\b[\w.+-]+@[\w.-]+\.[a-z]{2,}\b/gi, "[removed]")
    .replace(/(?:\+?\d[\d ()-]{7,}\d)/g, "[removed]")
    .replace(/\s+/g, " ").trim().slice(0, max) : "";
}
export function carMarkdown(body, availability) {
  const calendar=carAvailability(Number(body.car.id),availability);
  const details=carDetails(body,availability);
  const summary = carSummary(detailSummarySource(body));
  const c = body.car;
  const p = summary.prices;
  const money = (value, unit) => value == null ? null : `${value} ${p.currency} ${unit}`;
  const lines = [
    `# ${plainPublicText(summary.title, 150)}`,
    `${plainPublicText(summary.location.city, 100)}, ${plainPublicText(summary.location.region, 100)} (${summary.location.country_code})`,
    `Hire types: ${summary.rental_types.join(", ")}`,
    ...[
      money(p.self_drive_per_day, "per day (self drive)"),
      money(p.chauffeur_per_hour, "per hour (chauffeured)"),
      money(p.chauffeur_minimum, "minimum charge (chauffeured)"),
      money(p.event_per_hour, "per hour (event)"),
      money(p.event_per_day, "per day (event)"),
      money(p.event_from, "starting/minimum charge (event; not an hourly rate)"),
    ].filter(Boolean),
    publicDescription(body,300),
    `Rules: smoking ${c.nonsmoking === true ? "prohibited" : "not specified"}; pets ${c.nopets == null ? "not specified" : c.nopets ? "not allowed" : "allowed"}; decorations ${c.allow_decorations == null ? "not specified" : c.allow_decorations ? "allowed" : "not allowed"}.`,
    `Fuel policy: ${details.rules.fuel_policy || "not specified"}.`,
    `Availability window: ${calendar.window.available_from ?? "not stated"} to ${calendar.window.available_to ?? "not stated"}; minimum ${calendar.window.min_booking_days ?? "not stated"} days; notice ${calendar.window.advance_notice_days ?? "not stated"} days. ${calendar.blocked_dates.length} blocked date ranges; ${calendar.timed_constraints.length} timed constraints including preparation/turnaround buffers. Check live dates; a free date is not a reservation.`,
    `Delivery: ${details.delivery.enabled == null ? "not stated" : details.delivery.enabled ? `offered; self-drive ${details.delivery.self_drive_enabled == null ? "not stated" : details.delivery.self_drive_enabled ? "offered" : "not offered"}; maximum ${details.delivery.maximum_distance ?? "not stated"} ${details.delivery.distance_unit}; rate ${details.delivery.rate_per_distance_unit ?? "not stated"} ${p.currency} per ${details.delivery.distance_unit}; minimum ${details.delivery.minimum_fee ?? "not stated"} ${p.currency}` : "not offered"}. Confirm coverage and fees in checkout.`,
    `Insurance: ${plainPublicText(details.insurance,200)}${details.insurance?.length>200 ? "… (excerpt; confirm complete terms in checkout)" : ""}`,
    `Cancellation: ${plainPublicText(details.cancellation,200)}${details.cancellation?.length>200 ? "… (excerpt; confirm complete terms in checkout)" : ""}`,
    `Rating: ${summary.rating ?? "not yet rated"} (${summary.reviews_count ?? 0} reviews).`,
    `Booking link: ${bookingUrl(body)}`,
    summary.booking_notice ?? "",
    "Anonymous prices are estimates under current rules, not a reservation. Complete sign-in and checkout yourself on the booking website; never give an agent your login code.",
  ];
  const markdown = lines.filter(Boolean).join("\n\n") + "\n";
  if (markdown.length >= 3000) throw new Error("Car markdown exceeds public read budget");
  return markdown;
}
export function clientSlug(value) {
  if(typeof value === "string" && (value.includes("@") || /\d{7,}/.test(value)))return "unknown";
  return typeof value === "string" ? value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0,60) || "unknown" : "unknown";
}
export function bookingUrl(body, args = {}, client, transport = "api") {
  const source = detailSummarySource(body);
  let target = safeListingUrl(source);
  if (String(source.countryCode).toUpperCase() === "US") {
    const identity = body.car.publicCanonical?.on?.path;
    const legacy = body.car.legacySlug;
    const path = typeof legacy === "string" && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(legacy)
      ? `/car/${legacy}` : identity;
    if (!path || !/^\/(?:car\/[a-z0-9-]+|(?:[a-z]{2}\/)?cars\/\d+\/[a-z0-9-]+)\/?$/i.test(path))
      throw new Error("US booking identity is unavailable");
    target = new URL(path, "https://driveshare.com").href;
  }
  const url = new URL(target);
  const start = args.start ?? args.date_from, end = args.end ?? args.date_to ?? start;
  for (const [key,value] of [["start",start],["end",end]]) {
    if(value != null) {
      if(typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value) || !Number.isFinite(Date.parse(`${value}T00:00:00Z`)) || new Date(`${value}T00:00:00Z`).toISOString().slice(0,10) !== value) throw new Error("Invalid handoff dates");
      url.searchParams.set(key,value);
    }
  }
  if(start && end && end < start) throw new Error("End must not precede start");
  const hire = args.hire ?? args.rental_type ?? (start ? carSummary(source).rental_types[0] : undefined);
  if(start && args.hours!=null && ["chauffeured","event"].includes(hire)) {
    try { agentQuoteQuery({date_from:start,date_to:end,rental_type:hire,hours:args.hours}); }
    catch(error) {throw Object.assign(error,{status:400,code:"INVALID_HANDOFF",fix:"Use coherent dates/hours: chauffeured 1..23 same day; event 1..240 with matching end date."});}
  }
  if (hire != null) {
    if (!RENTAL_TYPES.includes(hire)) throw new Error("Invalid rental type");
    url.searchParams.set("hire", hire);
  }
  if (args.hours != null) {
    if (!Number.isInteger(args.hours) || args.hours < 1 || args.hours > 240) throw new Error("Invalid hours");
    url.searchParams.set("hours", String(args.hours));
  }
  url.searchParams.set("utm_source","ai_agent");
  url.searchParams.set("utm_medium", transport === "mcp" ? "mcp" : "api");
  url.searchParams.set("utm_campaign", transport === "mcp" ? clientSlug(client) : "api");
  return url.href;
}
export function carDetails(body, availability, args = {}) {
  const c = body.car;
  const summary = carSummary(detailSummarySource(body));
  const bool = v => typeof v === "boolean" ? v : null;
  const distanceUnit = ["US","GB"].includes(summary.location.country_code) ? "miles" : "km";
  return {
    ...summary, description: publicDescription(body),
    specs: { seats: number(c.passengers), transmission: text(c.transmission), body_style: text((body.carTypes ?? body.types)?.[0]?.name),
      engine: text(c.engine) ?? (number(c.engine_cc)>0 ? `${c.engine_cc} cc` : null) },
    rules: { smoking_allowed: c.nonsmoking===true ? false : null, pets_allowed: c.nopets==null ? null : !c.nopets, decorations_allowed: bool(c.allow_decorations),
       fuel_policy: publicDescription({...body,car:{...c,description:c.fuel_policy}},180) || null,
       mileage_limit: c.mileage_limit === true ? number(c.mileage_limit_miles) : null, mileage_unit: distanceUnit },
    delivery: { enabled: bool(c.delivery_enabled), self_drive_enabled: bool(c.self_drive_delivery_enabled),
      maximum_distance: number(c.delivery_maximum_distance), distance_unit: distanceUnit,
      rate_per_distance_unit:number(c.delivery_fee_per_unit),minimum_fee:number(c.delivery_fee_min),free:bool(c.delivery_free),
      free_radius:number(c.delivery_radius_free),full_fee_radius:number(c.delivery_radius_full) },
    booking_rules: carAvailability(c.id,availability).window,
    insurance: typeof c.insurance_included==="string" ? publicDescription({...body,car:{...c,description:c.insurance_included}},600) :
      c.insurance_included===true ? "The listing marks insurance as included; coverage and eligibility require checkout confirmation." :
      c.insurance_included===false ? "The listing does not include insurance; review the applicable coverage requirements in checkout." :
      "Listing coverage is not stated here. Review applicable insurance and eligibility requirements in human checkout.",
    cancellation: typeof c.cancellation_policy==="string" ? publicDescription({...body,car:{...c,description:c.cancellation_policy}},600) :
      "A listing-specific cancellation policy is not stated here. Review applicable terms in human checkout.",
    images: (body.images ?? []).map(i => i.cdn_medium ?? i.url).filter(i => typeof i === "string" && i.startsWith("https://")).slice(0,5),
    owner_first_name: typeof body.owner?.first_name === "string" ? plainPublicText(body.owner.first_name,80).split(" ")[0] || null : null,
    booking_url: bookingUrl(body,args),
  };
}
export function carAvailability(id, body) {
  if(!Array.isArray(body.blockedDates) || !Array.isArray(body.timedIntervals) || !body.window)
    throw new Error("Required calendar evidence is unavailable");
  const dates = body.blockedDates ?? [];
  const timed = body.timedIntervals ?? [];
  // Never trim constraints and silently represent a partial calendar as complete.
  if (dates.length > 1500 || timed.length > 1500) throw new Error("Calendar exceeds the public constraint budget");
  return { car_id: id, blocked_dates: dates.map(d => ({ start: d.start_date, end: d.end_date })),
    timed_constraints: timed.map(d => ({ start: d.start, end: d.end })), window: {
      min_booking_days:body.window.min_booking_days,advance_notice_days:body.window.advance_notice_days,
      available_from:body.window.available_from,available_to:body.window.available_to },
    scope: "Timed conflicts include preparation/turnaround buffers. Revalidate dates, minimum days and notice at checkout; no reservation is created." };
}
export function publicDescription(body, maximum=1500) {
  let description=plainPublicText(body.car?.description,4000);
  for(const privateValue of [body.owner?.last_name,body.owner?.email,body.owner?.phone,
    body.car?.address,body.car?.street,body.car?.zip]) {
    if(typeof privateValue!=="string" || privateValue.trim().length<3)continue;
    const escaped=privateValue.trim().replace(/[.*+?^${}()|[\]\\]/g,"\\$&");
    description=description.replace(new RegExp(escaped,"gi"),"[redacted]");
  }
  return description.replace(/\b\d{1,5}\s+(?:[a-z][\w.'-]*\s+){0,5}(?:street|st|road|rd|lane|ln|avenue|ave|drive|dr|court|close|crescent)\b/gi,"[address redacted]")
    .slice(0,maximum);
}
